import Model, { attr, belongsTo } from '@ember-data/model'
import { Type } from '@warp-drive/core-types/symbols'
import type Partner from 'teacher-ui-client/models/partner'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type StringTransform from 'teacher-ui-client/transforms/string'
import type BooleanTransform from 'teacher-ui-client/transforms/boolean'

export default class PartnerContactDetail extends Model {
  /* A partner can have different details per subscription type. This indicates which subtype these details are for,
   * for that partner. Note that a school often has multiple partners (potentially one per subtype as well)
   *
   * In order to choose the correct details you need for your subtype, first lookup the correct school-partner for your
   * subtype, then partner, then choose the correct contact details record for that partner for your subtype.
   *
   * eg, for maths:
   * school -> school-partner['maths'] -> partner -> partner-contact-details['maths']
   */
  @attr<StringTransform>('string') declare subscriptionType: SubscriptionType

  @belongsTo<Partner>('partner', { async: false, inverse: 'partnerContactDetails' }) declare partner?: Partner

  // School Support
  @attr<StringTransform>('string') declare schoolSupportEmail: string

  @attr<StringTransform>('string') declare schoolSupportPhone: string

  @attr<StringTransform>('string') declare schoolSupportPhoneHours: string

  @attr<BooleanTransform>('boolean') declare schoolSupportPhoneTollFree: boolean

  // School Sales

  @attr<StringTransform>('string') declare schoolSalesEmail: string

  @attr<StringTransform>('string') declare schoolSalesPhone: string

  @attr<StringTransform>('string') declare schoolSalesPhoneHours: string

  @attr<BooleanTransform>('boolean') declare schoolSalesPhoneTollFree: boolean

  // Note there are also parent contact fields available, but they are not needed for TUI.

  @attr<StringTransform>('string') declare cleverSupportEmail: string

  @attr<StringTransform>('string') declare address: string

  get schoolSupport() {
    return {
      email: this.schoolSupportEmail,
      phone: this.schoolSupportPhone,
      phoneHours: this.schoolSupportPhoneHours,
      phoneTollFree: this.schoolSupportPhoneTollFree,
      address: this.address,
    }
  }

  get schoolSales() {
    return {
      email: this.schoolSalesEmail,
      phone: this.schoolSalesPhone,
      phoneHours: this.schoolSalesPhoneHours,
      phoneTollFree: this.schoolSalesPhoneTollFree,
      address: this.address,
    }
  }

  [Type] = 'partner-contact-detail' as const
}
